import React, { useEffect, useState } from 'react';
import {
  Gauge, Database, Layers, FileCheck2, ShieldCheck, Info, CheckCircle2,
  Clock, MapPinned, UploadCloud,
} from 'lucide-react';
import {
  gisService,
  READINESS_DIMENSIONS,
  GIS_LAYER_CATEGORIES,
  GEOJSON_CONTRACT_FIELDS,
  IMPORT_QUALITY_READINESS,
  type ReadinessDimension,
  type PublicationStatus,
} from '@/services/gisService';
import { Badge } from '@/components/eds';

/**
 * GIS Data Integration & PostGIS Readiness visibility panel.
 *
 * - Consumes /api/admin/gis-readiness/overview (+ /executive) when authorized.
 * - When unauthorized or unavailable, renders a public-safe readiness
 *   explanation (NOT a raw backend error).
 * - Investor audience never sees admin-only / restricted detail.
 * - Invents NO GIS data, coordinates, concessions or mineral occurrences.
 */

export type ReadinessAudience = 'analyst' | 'investor';

interface GisReadinessPanelProps {
  audience?: ReadinessAudience;
}

/** Human-readable label + EDS tone for the dataset publication lifecycle. */
export const PUBLICATION_STATUS_META: Record<
  PublicationStatus,
  { label: string; tone: 'warning' | 'info' | 'success' | 'danger' | 'neutral' }
> = {
  pending_validation: { label: 'Pending Validation', tone: 'warning' },
  under_review: { label: 'Under Review', tone: 'info' },
  approved: { label: 'Approved', tone: 'success' },
  published: { label: 'Published', tone: 'success' },
  restricted: { label: 'Restricted', tone: 'danger' },
  archived: { label: 'Archived', tone: 'neutral' },
};

export const PublicationStatusBadge: React.FC<{
  status?: PublicationStatus;
}> = ({ status }) => {
  const meta = status ? PUBLICATION_STATUS_META[status] : null;

  if (!meta) {
    return (
      <Badge tone="neutral">
        Pending GIISDEC Validation
      </Badge>
    );
  }

  return (
    <Badge tone={meta.tone}>
      {meta.label}
    </Badge>
  );
};

const DimensionRow: React.FC<{ dim: ReadinessDimension }> = ({ dim }) => {
  const ready =
    dim.status === 'ready' ||
    (typeof dim.score === 'number' && dim.score >= 100);

  const pct =
    typeof dim.score === 'number'
      ? Math.max(0, Math.min(100, dim.score))
      : null;

  return (
    <div className="rounded-eds-md border border-eds-border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="font-eds-body text-sm font-medium text-eds-fg">
          {dim.label}
        </span>

        {ready ? (
          <CheckCircle2
            className="h-4 w-4 shrink-0 text-eds-success"
            aria-hidden="true"
          />
        ) : (
          <Clock
            className="h-4 w-4 shrink-0 text-eds-warning"
            aria-hidden="true"
          />
        )}
      </div>

      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-eds-pill bg-eds-surface-2">
        <div
          className={`h-full rounded-eds-pill ${
            ready ? 'bg-eds-success' : 'bg-eds-warning'
          }`}
          style={{ width: pct !== null ? `${pct}%` : '12%' }}
        />
      </div>

      <p className="mt-1.5 font-eds-body text-[11px] text-eds-muted-fg">
        {dim.note ?? (ready ? 'Ready' : 'Awaiting GIISDEC validation.')}
      </p>
    </div>
  );
};

const GisReadinessPanel: React.FC<GisReadinessPanelProps> = ({ audience = 'analyst' }) => {
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [dimensions, setDimensions] = useState<ReadinessDimension[]>(READINESS_DIMENSIONS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Investor audience consumes only the public-safe overview endpoint.
      const res = await gisService.getReadinessOverview().catch(() => ({
        authorized: false,
        overview: null,
      }));
      if (cancelled) return;
      setAuthorized(res.authorized);
      if (res.authorized && res.overview?.dimensions?.length) {
        setDimensions(res.overview.dimensions);
      } else {
        setDimensions(READINESS_DIMENSIONS);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="bg-eds-surface border border-eds-border rounded-eds-lg overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-eds-border bg-eds-surface-2">
        <h3 className="font-eds-heading font-semibold text-eds-fg flex items-center gap-2">
          <Gauge className="w-4 h-4 text-eds-success" /> GIS Data Integration &amp; PostGIS Readiness
        </h3>
        <span className="font-eds-mono text-xs text-eds-muted-fg">
          {loading ? 'checking…' : authorized ? 'live readiness' : 'public-safe view'}
        </span>
      </div>

      <div className="p-5 space-y-6">
        {/* 1 — Readiness Overview */}
        <section>
          <h4 className="font-eds-body text-xs font-semibold uppercase tracking-wide text-eds-muted-fg mb-2">
            Readiness Overview
          </h4>
          {authorized === false && (
            <div className="mb-3 rounded-eds-md border border-eds-border bg-eds-surface-2 px-3 py-2.5">
              <p className="font-eds-body text-sm font-medium text-eds-fg flex items-center gap-1.5">
                <Info className="w-4 h-4 text-eds-warning" />
                GIS readiness information is not currently available for this account.
              </p>
              <p className="font-eds-body text-xs text-eds-muted-fg mt-1">
                Approved GIS datasets and publication-ready layers will be displayed as they are
                validated and released by GIISDEC.
              </p>
              <p className="font-eds-body text-xs text-eds-muted-fg mt-1">
                Recommended action: continue using the available GIS catalog and permitted layers.
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {dimensions.map((d) => (
              <DimensionRow key={d.key} dim={d} />
            ))}
          </div>
        </section>

        {/* 2 — GeoJSON / PostGIS contract awareness */}
        <section>
          <h4 className="font-eds-body text-xs font-semibold uppercase tracking-wide text-eds-muted-fg mb-2 flex items-center gap-1.5">
            <Database className="w-3.5 h-3.5" /> GeoJSON / PostGIS Readiness
          </h4>
          <p className="font-eds-body text-sm text-eds-muted-fg mb-3">
            Approved spatial layers will be delivered through standardized, GeoJSON / PostGIS-ready
            contracts to ensure consistent, secure rendering across the platform.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {GEOJSON_CONTRACT_FIELDS.map((f) => (
              <div
                key={f.key}
                className="rounded-eds-sm border border-eds-border bg-eds-surface-2 px-3 py-2 font-eds-body text-xs text-eds-fg"
              >
                {f.label}
              </div>
            ))}
          </div>
        </section>

        {/* 4 — GIS layer categories readiness */}
        <section>
          <h4 className="font-eds-body text-xs font-semibold uppercase tracking-wide text-eds-muted-fg mb-2 flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5" /> Layer Categories
          </h4>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {GIS_LAYER_CATEGORIES.map((c) => (
              <li
                key={c.key}
                className="flex items-center justify-between gap-2 rounded-eds-sm border border-eds-border px-3 py-2"
              >
                <span className="flex items-center gap-2 font-eds-body text-sm text-eds-fg">
                  <MapPinned className="w-4 h-4 text-eds-muted-fg" /> {c.label}
                </span>
                <Badge tone="neutral" className="shrink-0">
                  Pending GIISDEC Validation
                </Badge>
              </li>
            ))}
          </ul>
        </section>

        {/* 5 — Import & data-quality readiness (analyst audience only) */}
        {audience === 'analyst' && (
          <section>
            <h4 className="font-eds-body text-xs font-semibold uppercase tracking-wide text-eds-muted-fg mb-2 flex items-center gap-1.5">
              <UploadCloud className="w-3.5 h-3.5" /> Import &amp; Data Quality Readiness
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {IMPORT_QUALITY_READINESS.map((q) => (
                <div
                  key={q.key}
                  className="flex items-center gap-2 rounded-eds-sm border border-eds-border px-3 py-2 font-eds-body text-xs text-eds-fg"
                >
                  <FileCheck2 className="w-4 h-4 text-eds-success shrink-0" />
                  <span>{q.label}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 6 — Investor public-safe assurance */}
        {audience === 'investor' && (
          <section className="rounded-eds-md border border-eds-success/30 bg-eds-success/8 px-3 py-2.5">
            <p className="font-eds-body text-sm font-medium text-eds-fg flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4" />
              You are viewing public-safe GIS readiness information.
            </p>
            <p className="font-eds-body text-xs text-eds-muted-fg mt-1">
              Restricted metadata, confidential layers, and internal classifications are not exposed
              in the investor experience.
            </p>
          </section>
        )}
      </div>
    </div>
  );
};

export default GisReadinessPanel;
